import {NoopLogger} from "@cm/log"
import type {CmLogger} from "@cm/log"
import type {Catalog, CatalogDiff, CatalogItem, CatalogPrice, GetSubgraphCallback} from "#pricing/catalogs/catalog-interface"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import {Currency} from "#pricing/types/currency"
import type {PriceEntry, PriceMap} from "#pricing/types/pricing-types"
import {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import type {utils, WorkSheet} from "xlsx"

export class CalypsoCatalog implements Catalog {
    items: CalypsoCatalogItem[]

    constructor(items: CalypsoCatalogItem[]) {
        this.items = items
    }

    search(query: string): CatalogItem[] {
        const lowerCaseQuery = query.toLowerCase()
        return this.items.filter((item) => item.description.toLowerCase().includes(lowerCaseQuery) || item.uniqueId.toLowerCase().includes(lowerCaseQuery))
    }

    getPriceMap(): PriceMap {
        const priceMap: PriceMap = new Map()
        this.items.forEach((item) => priceMap.set(item.uniqueId, item.prices))
        return priceMap
    }

    getAllPrices(): CatalogPrice[] {
        const result: CatalogPrice[] = []
        this.items.forEach((item) => {
            item.prices.forEach(({price, priceIncludesVat}, currency) => {
                result.push({uniqueId: item.uniqueId, price, currency, priceIncludesVat})
            })
        })
        return result
    }

    diff(_newCatalog: Catalog): CatalogDiff {
        throw new Error("CalypsoCatalog.diff is not implemented")
    }
}

class CalypsoCatalogItem implements CatalogItem {
    sku: string
    desc1: string
    desc2: string
    desc4: string
    prices: Map<Currency, PriceEntry>

    constructor(sku: string, desc1: string, desc2: string, desc4: string, prices: Map<Currency, PriceEntry>) {
        this.sku = sku
        this.desc1 = desc1
        this.desc2 = desc2
        this.desc4 = desc4
        this.prices = prices
    }

    get uniqueId() {
        return this.sku
    }

    get description() {
        return [this.desc1, this.desc2, this.desc4]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(", ")
    }

    createPriceGraph(getSubgraph: GetSubgraphCallback, withCondition = true): PricedItemNode {
        return new PricedItemNode({
            parameters: {
                description: this.description,
                uniqueId: this.uniqueId,
                condition: withCondition
                    ? new VariantConditionNode({
                          parameters: {
                              condition: {variantIds: [], variantOperator: "AND", negated: false},
                              currentConfigurations: new ConfigurationList({parameters: {list: []}}),
                          },
                      })
                    : undefined,
                subPrices: undefined,
                sku: this.uniqueId,
                dependsOnSubprice: false,
                amount: 1,
            },
        })
    }
}

const COL_PART = "Part"
const COL_RRP_INC_VAT = "RRP Inc VAT"

const SHEET_NAME = "Full List"

const COLS = [COL_PART, "Legacy Code", "desc1", "desc2", "desc3", "desc4", "Kit/Barcode", "List Price Ex VAT", COL_RRP_INC_VAT]

export function parseXlsx(xlsxBuf: Uint8Array, xlsx: typeof import("xlsx"), logger: CmLogger = NoopLogger): Catalog {
    const workbook = xlsx.read(xlsxBuf, {type: "buffer", cellDates: true, cellStyles: true, cellNF: true, cellText: true})
    const worksheet = workbook.Sheets[SHEET_NAME]
    if (!worksheet) throw new Error(`Sheet "${SHEET_NAME}" not found. Available sheets: ${workbook.SheetNames.join(", ")}`)

    const headerRowIndex = 0

    verifyXlsx(worksheet, headerRowIndex, COLS, xlsx)

    const range = xlsx.utils.decode_range(worksheet["!ref"]!)
    range.s.r = headerRowIndex + 1 // Skip header row for loading data

    const newRange = xlsx.utils.encode_range(range)
    const rows = xlsx.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
        range: newRange,
        header: COLS,
        blankrows: false,
    })

    const filledRows = rows.filter((row) => row[COL_PART] != null && row[COL_RRP_INC_VAT] != null)

    logger.log(`Skipped ${rows.length - filledRows.length} empty rows`)

    const catalogItems = filledRows.map(
        (row) =>
            new CalypsoCatalogItem(
                String(row[COL_PART]).trim(),
                String(row["desc1"] ?? ""),
                String(row["desc2"] ?? ""),
                String(row["desc4"] ?? ""),
                new Map([[Currency.Gbp, {price: Number(row[COL_RRP_INC_VAT]), priceIncludesVat: true}]]),
            ),
    )

    verifyData(catalogItems)

    return new CalypsoCatalog(catalogItems)
}

function verifyXlsx(worksheet: WorkSheet, headerRowIndex: number, expectedColTitles: string[], xlsx: {utils: typeof utils}) {
    expectedColTitles.forEach((title, index) => {
        const cellValue = worksheet[xlsx.utils.encode_cell({r: headerRowIndex, c: index})]?.v?.toString().trim()
        if (cellValue !== title) throw new Error(`Expected ${title} in cell ${xlsx.utils.encode_col(index)}${headerRowIndex + 1}, but got ${cellValue}`)
    })
}

function verifyData(catalogItems: CalypsoCatalogItem[]) {
    const uniqueIds = new Set<string>()
    catalogItems.forEach((item) => {
        if (uniqueIds.has(item.uniqueId)) throw new Error(`Duplicate uniqueId: ${item.uniqueId}`)
        uniqueIds.add(item.uniqueId)
    })
}
